// ============================================================
// FONADHOO CRM v3.0 - CAMPAIGN TRIPS COMPONENT
// Track campaign trips: destination, travelers, cost
// ============================================================

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDate, daysUntil } from './shared/utils';
import SearchableSelect from './shared/SearchableSelect';
import { Icon } from './shared/Icon';

const SUPPORTERS = new Set(['Strong Support', 'Leaning Support']);
const NEG_OUTCOMES = new Set(['No Answer / Not Home', 'Busy / Call Back Later', 'Refused to Talk', 'Hostile Response']);
const waShare = (text) => `https://wa.me/?text=${encodeURIComponent(text)}`;

const BLANK_FORM = {
  title: '',
  atoll: '',
  islands: [],
  start_date: '',
  end_date: '',
  travelers: [], // [{type:'voter'|'manual', voter_id?, name}]
  cost: '',
  status: 'planned',
  notes: '',
  itinerary: [], // [{date, plan}]
  checklist: [], // [{label, done}]
};

const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: '6px',
  border: '1px solid var(--border-2)', background: 'var(--surface-2)',
  color: 'var(--text)', fontSize: '14px',
};

export default function CampaignTrips({ supabase, profile, user, allLocations = [], allVoters = [] }) {
  profile = profile ?? user;
  const navigate = useNavigate();

  const [trips, setTrips] = useState([]);
  const [allTrips, setAllTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterView, setFilterView] = useState('All');
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState(BLANK_FORM);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(BLANK_FORM);
  const [pendingId, setPendingId] = useState(null);
  const [expandedAtolls, setExpandedAtolls] = useState(() => new Set());
  const toggleAtoll = (key) => setExpandedAtolls(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const [outreach, setOutreach] = useState([]);
  const [history, setHistory] = useState([]);
  const voterById = useMemo(() => new Map((allVoters || []).map(v => [v.id, v])), [allVoters]);

  useEffect(() => { loadTrips(); }, [filterView]);

  // For auto-measured trip outcomes (contacts/commitments/support gained).
  useEffect(() => {
    const sinceDate = new Date(Date.now() - 180 * 86400000).toISOString().split('T')[0];
    const sinceTs = new Date(Date.now() - 180 * 86400000).toISOString();
    Promise.all([
      supabase.from('outreach').select('voter_id, date, outcome').gte('date', sinceDate),
      supabase.from('support_history').select('voter_id, changed_from, changed_to, changed_at').gte('changed_at', sinceTs),
    ]).then(([o, h]) => {
      if (o.error) console.error('[trips] outreach load failed:', o.error);
      if (h.error) console.error('[trips] support_history load failed:', h.error);
      setOutreach(o.data || []); setHistory(h.data || []);
    });
  }, [supabase]);

  // Outcomes for a trip: outreach + commitments + support gained within its
  // island(s), between start and (end + 14 days). Auto — no manual entry.
  const tripOutcomes = (trip) => {
    const islSet = new Set((trip.islands || []).filter(Boolean));
    if (islSet.size === 0 || !trip.start_date) return null;
    const start = new Date(trip.start_date).getTime();
    const end = (trip.end_date ? new Date(trip.end_date).getTime() : start) + 14 * 86400000;
    const inScope = (voterId, ts) => {
      if (ts < start || ts > end) return false;
      const v = voterById.get(voterId);
      return v && islSet.has(v.island);
    };
    let contacts = 0, commitments = 0;
    for (const o of outreach) {
      const ts = o.date ? new Date(o.date).getTime() : 0;
      if (!inScope(o.voter_id, ts)) continue;
      if (!NEG_OUTCOMES.has(o.outcome)) contacts++;
      if (o.outcome === 'Commitment Made') commitments++;
    }
    let gained = 0;
    for (const h of history) {
      if (!(SUPPORTERS.has(h.changed_to) && !SUPPORTERS.has(h.changed_from))) continue;
      if (!inScope(h.voter_id, new Date(h.changed_at).getTime())) continue;
      gained++;
    }
    return { contacts, commitments, gained };
  };

  const toggleChecklistItem = async (trip, idx) => {
    const next = (trip.checklist || []).map((c, i) => i === idx ? { ...c, done: !c.done } : c);
    setTrips(prev => prev.map(t => t.id === trip.id ? { ...t, checklist: next } : t));
    await supabase.from('campaign_trips').update({ checklist: next }).eq('id', trip.id);
  };

  const shareTrip = (trip) => {
    const lines = [
      `*${trip.title}*`,
      `Dates: ${renderDateRange(trip)}`,
      trip.islands?.length ? `Islands: ${trip.islands.join(', ')}` : null,
      trip.travelers?.length ? `Travelers: ${trip.travelers.map(t => t.name).join(', ')}` : null,
    ].filter(Boolean);
    window.open(waShare(lines.join('\n')), '_blank');
  };

  const loadTrips = async () => {
    setLoading(true);
    const today = new Date().toISOString().split('T')[0];

    let query = supabase.from('campaign_trips').select('*');
    if (filterView === 'Upcoming')        query = query.gte('start_date', today).neq('status', 'completed').neq('status', 'cancelled').order('start_date', { ascending: true });
    else if (filterView === 'Backlog')    query = query.lt('start_date', today).neq('status', 'completed').neq('status', 'cancelled').order('start_date', { ascending: false });
    else if (filterView === 'Completed')  query = query.eq('status', 'completed').order('start_date', { ascending: false });
    else                                   query = query.order('start_date', { ascending: false });

    const [{ data }, { data: all }] = await Promise.all([
      query,
      supabase.from('campaign_trips').select('id, start_date, cost, status'),
    ]);

    setTrips(data || []);
    setAllTrips(all || []);
    setLoading(false);
  };

  const today = new Date().toISOString().split('T')[0];
  const stats = {
    total:     allTrips.length,
    upcoming:  allTrips.filter(t => t.start_date >= today && t.status !== 'cancelled' && t.status !== 'completed').length,
    backlog:   allTrips.filter(t => t.start_date <  today && t.status !== 'cancelled' && t.status !== 'completed').length,
    completed: allTrips.filter(t => t.status === 'completed').length,
    cost:      allTrips.reduce((s, t) => s + (Number(t.cost) || 0), 0),
  };

  const handleAdd = async () => {
    if (!addForm.title || !addForm.start_date) {
      alert('Title and start date are required');
      return;
    }
    const { error } = await supabase.from('campaign_trips').insert({
      title: addForm.title,
      atoll: addForm.atoll || null,
      islands: addForm.islands || [],
      start_date: addForm.start_date,
      end_date: addForm.end_date || null,
      travelers: addForm.travelers || [],
      cost: parseFloat(addForm.cost) || 0,
      status: addForm.status,
      notes: addForm.notes,
      itinerary: addForm.itinerary || [],
      checklist: addForm.checklist || [],
      created_by: profile?.id,
    });
    if (error) { alert('Error: ' + error.message); return; }
    setShowAdd(false);
    setAddForm(BLANK_FORM);
    loadTrips();
  };

  const handleSaveEdit = async (id) => {
    if (!editForm.title || !editForm.start_date) {
      alert('Title and start date are required');
      return;
    }
    setPendingId(id);
    const { error } = await supabase.from('campaign_trips').update({
      title: editForm.title,
      atoll: editForm.atoll || null,
      islands: editForm.islands || [],
      start_date: editForm.start_date,
      end_date: editForm.end_date || null,
      travelers: editForm.travelers || [],
      cost: parseFloat(editForm.cost) || 0,
      status: editForm.status,
      notes: editForm.notes,
      itinerary: editForm.itinerary || [],
      checklist: editForm.checklist || [],
    }).eq('id', id);
    setPendingId(null);
    if (error) { alert('Save failed: ' + error.message); return; }
    setEditingId(null);
    loadTrips();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this trip?')) return;
    setPendingId(id);
    const { error } = await supabase.from('campaign_trips').delete().eq('id', id);
    setPendingId(null);
    if (error) { alert('Delete failed: ' + error.message); return; }
    loadTrips();
  };

  const handleStatus = async (id, status) => {
    setPendingId(id);
    const { error } = await supabase.from('campaign_trips').update({ status }).eq('id', id);
    setPendingId(null);
    if (error) { alert('Update failed: ' + error.message); return; }
    setAllTrips(prev => prev.map(t => t.id === id ? { ...t, status } : t));
    // Reload so the trip moves out of the current filtered view (e.g. an
    // Upcoming trip marked Completed should leave the Upcoming list).
    loadTrips();
  };

  const startEdit = (trip) => {
    setEditingId(trip.id);
    setEditForm({
      title: trip.title || '',
      atoll: trip.atoll || '',
      islands: trip.islands || [],
      start_date: trip.start_date || '',
      end_date: trip.end_date || '',
      travelers: trip.travelers || [],
      cost: trip.cost || '',
      status: trip.status || 'planned',
      notes: trip.notes || '',
      itinerary: trip.itinerary || [],
      checklist: trip.checklist || [],
    });
  };

  const getStatusColor = (trip) => {
    if (trip.status === 'cancelled') return 'var(--text-3)';
    if (trip.status === 'completed') return '#10b981';
    const days = daysUntil(trip.start_date);
    if (days < 0) return 'var(--text-3)';
    if (days === 0) return '#10b981';
    if (days <= 7) return '#f59e0b';
    return '#3b82f6';
  };

  const getStatusLabel = (trip) => {
    if (trip.status === 'cancelled') return 'Cancelled';
    if (trip.status === 'completed') return 'Completed';
    const days = daysUntil(trip.start_date);
    if (days < 0) return 'Past';
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    return `In ${days} days`;
  };

  const renderDateRange = (trip) => {
    const start = formatDate(trip.start_date);
    if (!trip.end_date || trip.end_date === trip.start_date) return start;
    return `${start} → ${formatDate(trip.end_date)}`;
  };

  return (
    <div>
      {/* Header */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)', borderRadius: '14px', padding: '20px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
            <span style={{ width: 38, height: 38, borderRadius: 11, display: 'grid', placeItems: 'center', background: '#10b9811f', color: '#10b981', flexShrink: 0 }}>
              <Icon name="plane" size={20} />
            </span>
            <div>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10.5, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 2 }}>Field Travel</div>
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '26px', fontWeight: 600, margin: 0, color: 'var(--text-strong)', letterSpacing: '-0.3px', lineHeight: 1 }}>Trips</h2>
            </div>
          </div>
          <button onClick={() => setShowAdd(!showAdd)} style={{
            padding: '10px 18px', borderRadius: '9px', border: 'none',
            background: showAdd ? 'var(--border-2)' : '#3b82f6', color: 'white', fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap',
            fontFamily: "'DM Mono', monospace", display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'filter 0.15s'
          }}
            onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.12)'}
            onMouseLeave={e => e.currentTarget.style.filter = 'none'}
          >{showAdd ? 'Cancel' : <><Icon name="plus" size={15} color="#fff" /> Create Trip</>}</button>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px', marginBottom: '20px' }}>
          {[
            { value: stats.total,     label: 'Total Trips', color: '#3b82f6', filter: 'All',       icon: 'plane' },
            { value: stats.upcoming,  label: 'Upcoming',    color: '#f59e0b', filter: 'Upcoming',  icon: 'clock' },
            { value: stats.backlog,   label: 'Backlog',     color: '#ef4444', filter: 'Backlog',   icon: 'activity' },
            { value: stats.completed, label: 'Completed',   color: '#10b981', filter: 'Completed', icon: 'check' },
            { value: `MVR ${stats.cost.toLocaleString()}`, label: 'Total Cost', color: '#8b5cf6', filter: null, icon: 'coins' },
          ].map(({ value, label, color, filter, icon }) => {
            const on = filter && filterView === filter;
            return (
              <div key={label} onClick={filter ? () => setFilterView(filter) : undefined}
                style={{
                  background: 'var(--surface-2)', padding: '14px 16px', borderRadius: '12px',
                  minHeight: 96, position: 'relative', overflow: 'hidden',
                  cursor: filter ? 'pointer' : 'default',
                  border: on ? `1px solid ${color}` : '1px solid var(--border)',
                  boxShadow: on ? `0 0 0 1px ${color}` : 'none',
                  transition: 'border-color 0.15s, box-shadow 0.15s, transform 0.15s',
                }}
                onMouseEnter={e => { if (filter) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.borderColor = color; } }}
                onMouseLeave={e => { if (filter) { e.currentTarget.style.transform = 'none'; e.currentTarget.style.borderColor = on ? color : 'var(--border)'; } }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9.5, letterSpacing: 0.6, textTransform: 'uppercase', color: 'var(--text-2)', fontWeight: 500, lineHeight: 1.2 }}>{label}</span>
                  <span style={{ width: 28, height: 28, borderRadius: 8, display: 'grid', placeItems: 'center', background: color + '1f', color, flexShrink: 0 }}>
                    <Icon name={icon} size={15} />
                  </span>
                </div>
                <div style={{ fontSize: typeof value === 'string' ? '18px' : '28px', fontWeight: '700', color, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.5px' }}>{value}</div>
                {filter && <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: color, opacity: on ? 1 : 0, transition: 'opacity 0.15s' }} />}
              </div>
            );
          })}
        </div>

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {['All', 'Upcoming', 'Backlog', 'Completed'].map(view => {
            const on = filterView === view;
            return (
              <button key={view} onClick={() => setFilterView(view)} style={{
                padding: '8px 16px', borderRadius: '9px',
                border: on ? '1px solid rgba(59,130,246,0.55)' : '1px solid var(--border)',
                background: on ? 'rgba(59,130,246,0.13)' : 'transparent',
                color: on ? '#60a5fa' : 'var(--text-2)',
                fontSize: '12.5px', fontWeight: '600', cursor: 'pointer',
                fontFamily: "'DM Mono', monospace",
                transition: 'background 0.15s, border-color 0.15s, color 0.15s',
              }}
                onMouseEnter={e => { if (!on) { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.color = 'var(--text)'; } }}
                onMouseLeave={e => { if (!on) { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-2)'; } }}
              >{view}</button>
            );
          })}
        </div>
      </div>

      {/* Add Form */}
      {showAdd && (
        <div style={{ background: 'var(--surface)', borderRadius: '14px', padding: '20px', marginBottom: '20px', border: '1px solid rgba(59,130,246,0.5)', boxShadow: 'var(--shadow-md)' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '16px', color: 'var(--text-strong)' }}>Create New Trip</h3>
          <TripForm form={addForm} setForm={setAddForm} onSubmit={handleAdd} onCancel={() => { setShowAdd(false); setAddForm(BLANK_FORM); }} submitLabel="Create Trip" allLocations={allLocations} allVoters={allVoters} />
        </div>
      )}

      {/* Trips list */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-2)' }}>
          <div style={{ width: '40px', height: '40px', border: '3px solid var(--border-2)', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
          Loading trips...
        </div>
      ) : (() => {
        // Group trips by atoll. Trips without an atoll go under "__no_atoll__".
        const grouped = {};
        for (const tr of trips) {
          const key = tr.atoll || '__no_atoll__';
          if (!grouped[key]) grouped[key] = [];
          grouped[key].push(tr);
        }
        const atollKeys = Object.keys(grouped).filter(k => k !== '__no_atoll__').sort();
        const sections = [...atollKeys, ...(grouped['__no_atoll__'] ? ['__no_atoll__'] : [])];

        const renderTripCard = (trip) => {
            const color = getStatusColor(trip);
            const isEditing = editingId === trip.id;
            const isPending = pendingId === trip.id;

            return (
              <div key={trip.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)', borderRadius: '12px', padding: '20px', borderLeft: `4px solid ${color}` }}>
                {isEditing ? (
                  <>
                    <div style={{ marginBottom: '12px', fontWeight: '600', color: 'var(--text-2)' }}>Editing: {trip.title}</div>
                    <TripForm form={editForm} setForm={setEditForm}
                      onSubmit={() => handleSaveEdit(trip.id)}
                      submitLabel={isPending ? 'Saving…' : 'Save Changes'}
                      disabled={isPending}
                      onCancel={() => setEditingId(null)}
                      allLocations={allLocations}
                      allVoters={allVoters}
                    />
                  </>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ fontSize: '17px', fontWeight: '700', marginBottom: '8px', color: 'var(--text-strong)' }}>{trip.title}</h4>
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                        {trip.atoll && <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', background: '#3b82f620', color: '#3b82f6', display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon name="mapPin" size={12} /> {trip.atoll}</span>}
                        <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', background: color + '20', color }}>{getStatusLabel(trip)}</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 7, fontSize: '13px', color: 'var(--text-2)' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Icon name="calendar" size={14} color="var(--text-4)" /> {renderDateRange(trip)}</span>
                        {trip.islands?.length > 0 && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Icon name="island" size={14} color="var(--text-4)" /> {trip.islands.join(', ')}</span>
                        )}
                        {trip.travelers?.length > 0 && (() => {
                          const leader = trip.travelers.find(t => t.is_leader);
                          const others = trip.travelers.filter(t => !t.is_leader);
                          return (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              <Icon name="users" size={14} color="var(--text-4)" />
                              <span>{trip.travelers.length} traveler{trip.travelers.length !== 1 ? 's' : ''}</span>
                              {leader && (
                                <span
                                  onClick={leader.voter_id != null ? (e) => { e.stopPropagation(); navigate('/voter/' + leader.voter_id); } : undefined}
                                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#fbbf24', fontWeight: 600, cursor: leader.voter_id != null ? 'pointer' : 'default', textDecoration: leader.voter_id != null ? 'underline' : 'none' }}
                                ><Icon name="crown" size={13} /> {leader.name}</span>
                              )}
                              {others.length > 0 && (
                                <span style={{ color: 'var(--text-3)' }}>
                                  {leader ? '+ ' : ''}
                                  {others.map((t, i) => (
                                    <React.Fragment key={t.voter_id ?? t.name ?? i}>
                                      {i > 0 && ', '}
                                      <span
                                        onClick={t.voter_id != null ? (e) => { e.stopPropagation(); navigate('/voter/' + t.voter_id); } : undefined}
                                        style={{ cursor: t.voter_id != null ? 'pointer' : 'default', textDecoration: t.voter_id != null ? 'underline' : 'none' }}
                                      >{t.name}</span>
                                    </React.Fragment>
                                  ))}
                                </span>
                              )}
                            </span>
                          );
                        })()}
                        {trip.cost > 0 && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Icon name="coins" size={14} color="var(--text-4)" /> MVR {Number(trip.cost).toLocaleString()}</span>
                        )}
                      </div>
                      {trip.notes && <p style={{ marginTop: '10px', fontSize: '14px', color: 'var(--text)' }}>{trip.notes}</p>}

                      {/* Day-by-day itinerary */}
                      {trip.itinerary?.length > 0 && (
                        <div style={{ marginTop: 12 }}>
                          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text-4)', marginBottom: 6 }}>Itinerary</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {trip.itinerary.map((d, i) => (
                              <div key={i} style={{ display: 'flex', gap: 8, fontSize: 13, color: 'var(--text-2)' }}>
                                <span style={{ color: 'var(--link)', fontFamily: "'DM Mono', monospace", fontSize: 11, minWidth: 72, flexShrink: 0 }}>{d.date ? formatDate(d.date) : `Day ${i + 1}`}</span>
                                <span>{d.plan}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Logistics checklist (tap to toggle) */}
                      {trip.checklist?.length > 0 && (
                        <div style={{ marginTop: 12 }}>
                          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text-4)', marginBottom: 6 }}>
                            Checklist · {trip.checklist.filter(c => c.done).length}/{trip.checklist.length}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                            {trip.checklist.map((c, i) => (
                              <button key={i} type="button" onClick={() => toggleChecklistItem(trip, i)}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left' }}>
                                <span style={{ width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${c.done ? '#10b981' : 'var(--border-2)'}`, background: c.done ? '#10b981' : 'transparent', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                                  {c.done && <Icon name="check" size={11} color="#fff" />}
                                </span>
                                <span style={{ fontSize: 13, color: c.done ? 'var(--text-4)' : 'var(--text)', textDecoration: c.done ? 'line-through' : 'none' }}>{c.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Auto-measured outcomes for past/ongoing trips */}
                      {(() => {
                        const o = daysUntil(trip.start_date) <= 0 ? tripOutcomes(trip) : null;
                        if (!o) return null;
                        const stat = (icon, col, val, lab) => (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <Icon name={icon} size={14} color={col} />
                            <span style={{ fontWeight: 700, color: col, fontVariantNumeric: 'tabular-nums' }}>{val}</span>
                            <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{lab}</span>
                          </span>
                        );
                        return (
                          <div style={{ marginTop: 12, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', padding: '10px 12px', background: 'var(--surface-2)', borderRadius: 8, border: '1px solid var(--border)' }}>
                            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text-4)' }}>Outcomes</span>
                            {stat('phone', '#3b82f6', o.contacts, 'contacts')}
                            {stat('target', '#f59e0b', o.commitments, 'commitments')}
                            {stat('trending', '#10b981', `+${o.gained}`, 'support')}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '90px' }}>
                      {trip.status !== 'completed' && trip.status !== 'cancelled' && (
                        <button onClick={() => handleStatus(trip.id, 'completed')} disabled={isPending}
                          style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: isPending ? 'var(--border-2)' : '#10b981', color: 'white', fontSize: '13px', fontWeight: '600', cursor: isPending ? 'not-allowed' : 'pointer', opacity: isPending ? 0.6 : 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                          <Icon name="check" size={14} color="#fff" /> Complete
                        </button>
                      )}
                      <button onClick={() => shareTrip(trip)} disabled={isPending}
                        style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid var(--border-2)', background: 'transparent', color: 'var(--text-2)', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                        <Icon name="message" size={13} /> Share
                      </button>
                      <button onClick={() => startEdit(trip)} disabled={isPending}
                        style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid var(--border-2)', background: 'transparent', color: 'var(--text-2)', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                        Edit
                      </button>
                      <button onClick={() => handleDelete(trip.id)} disabled={isPending}
                        style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid var(--border-2)', background: 'transparent', color: isPending ? 'var(--text-3)' : '#ef4444', fontSize: '13px', fontWeight: '600', cursor: isPending ? 'not-allowed' : 'pointer', opacity: isPending ? 0.6 : 1 }}>
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
        };

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {sections.map(key => {
              const isNoAtoll = key === '__no_atoll__';
              const isCollapsed = !expandedAtolls.has(key);
              const headColor = isNoAtoll ? '#ef4444' : 'var(--link)';
              return (
                <div key={key}>
                  <div onClick={() => toggleAtoll(key)}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: isCollapsed ? 0 : 10, cursor: 'pointer', userSelect: 'none' }}>
                    <Icon name="mapPin" size={13} color={headColor} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: headColor, textTransform: 'uppercase', letterSpacing: '0.8px', fontFamily: "'DM Mono', monospace" }}>
                      {isNoAtoll ? 'Unspecified Atoll' : key}
                    </span>
                    <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                    <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{grouped[key].length} trip{grouped[key].length !== 1 ? 's' : ''}</span>
                    <span style={{ fontSize: 11, color: 'var(--text-4)', marginLeft: 4 }}>{isCollapsed ? '▸' : '▾'}</span>
                  </div>
                  {!isCollapsed && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {grouped[key].map(renderTripCard)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })()}

      {!loading && trips.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-3)' }}>
          <div style={{ display: 'grid', placeItems: 'center', marginBottom: '14px' }}><Icon name="plane" size={42} color="var(--text-4)" /></div>
          <p>No {filterView.toLowerCase()} trips</p>
          {filterView === 'Upcoming' && <p style={{ fontSize: '13px', marginTop: '8px' }}>Click "+ Create Trip" to add one</p>}
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function TripForm({ form, setForm, onSubmit, submitLabel, disabled, onCancel, allLocations, allVoters }) {
  const [voterSearch, setVoterSearch] = useState('');
  const [manualName, setManualName] = useState('');
  const [customDest, setCustomDest] = useState('');

  // Atolls and islands are derived from the actual voter data, so the dropdowns
  // only show places that are part of the current campaign. Falls back to the
  // locations table so you can still pick places before voters are imported.
  const atolls = (() => {
    const fromVoters = (allVoters || []).map(v => v.atoll).filter(Boolean);
    const fromDb    = (allLocations || []).map(l => l.atoll).filter(Boolean);
    return [...new Set([...fromVoters, ...fromDb])].sort();
  })();
  const islandsForAtoll = form.atoll
    ? (() => {
        const fromVoters = (allVoters || []).filter(v => v.atoll === form.atoll).map(v => v.island).filter(Boolean);
        const fromDb     = (allLocations || []).filter(l => l.atoll === form.atoll).map(l => l.island).filter(Boolean);
        return [...new Set([...fromVoters, ...fromDb])].sort();
      })()
    : [];
  const allDestinations = (() => {
    const fromVoters = (allVoters || []).map(v => v.island).filter(Boolean);
    const fromDb     = (allLocations || []).map(l => l.island).filter(Boolean);
    const all = [...new Set([...fromVoters, ...fromDb])].sort();
    if (!form.atoll) return all;
    const inAtoll = new Set(islandsForAtoll);
    return [...islandsForAtoll, ...all.filter(i => !inAtoll.has(i))];
  })();

  const label = (text) => (
    <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: 'var(--text-2)' }}>{text}</label>
  );

  const toggleIsland = (island) => {
    const cur = form.islands || [];
    setForm({ ...form, islands: cur.includes(island) ? cur.filter(i => i !== island) : [...cur, island] });
  };

  const addVoterTraveler = (voter) => {
    if (form.travelers.find(t => t.voter_id === voter.id)) return;
    setForm({ ...form, travelers: [...form.travelers, { type: 'voter', voter_id: voter.id, name: voter.name }] });
    setVoterSearch('');
  };

  const addManualTraveler = () => {
    const n = manualName.trim();
    if (!n) return;
    setForm({ ...form, travelers: [...form.travelers, { type: 'manual', name: n }] });
    setManualName('');
  };

  const removeTraveler = (idx) => {
    setForm({ ...form, travelers: form.travelers.filter((_, i) => i !== idx) });
  };

  const toggleLeader = (idx) => {
    setForm({
      ...form,
      travelers: form.travelers.map((t, i) => ({ ...t, is_leader: i === idx ? !t.is_leader : false })),
    });
  };

  const addItineraryDay = () => setForm({ ...form, itinerary: [...(form.itinerary || []), { date: '', plan: '' }] });
  const updateItinerary = (i, key, val) => setForm({ ...form, itinerary: (form.itinerary || []).map((d, j) => j === i ? { ...d, [key]: val } : d) });
  const removeItinerary = (i) => setForm({ ...form, itinerary: (form.itinerary || []).filter((_, j) => j !== i) });
  const addChecklistItem = () => setForm({ ...form, checklist: [...(form.checklist || []), { label: '', done: false }] });
  const updateChecklist = (i, val) => setForm({ ...form, checklist: (form.checklist || []).map((c, j) => j === i ? { ...c, label: val } : c) });
  const removeChecklist = (i) => setForm({ ...form, checklist: (form.checklist || []).filter((_, j) => j !== i) });

  const voterResults = voterSearch.trim().length >= 2
    ? (allVoters || [])
        .filter(v => v.name?.toLowerCase().includes(voterSearch.toLowerCase()))
        .filter(v => !form.travelers.find(t => t.voter_id === v.id))
        .slice(0, 8)
    : [];

  return (
    <div style={{ display: 'grid', gap: '16px' }}>
      <div>
        {label('Trip Title *')}
        <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} style={inputStyle} placeholder="e.g., Fonadhoo Visit – Oct 2025" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
        <div>
          {label('Atoll')}
          <select value={form.atoll} onChange={e => setForm({ ...form, atoll: e.target.value, islands: [] })} style={inputStyle}>
            <option value="">— Select atoll —</option>
            {atolls.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div>
          {label('Status')}
          <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} style={inputStyle}>
            <option value="planned">Planned</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      <div>
        {label('Islands / Cities (optional)')}
        {/* Selected destinations as chips */}
        {form.islands?.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            {form.islands.map(d => (
              <div key={d} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--border)', border: '1px solid #3b82f6', borderRadius: 14, padding: '3px 10px', fontSize: 12, color: 'var(--link)' }}>
                <span>{d}</span>
                <button type="button" onClick={() => toggleIsland(d)}
                  style={{ background: 'none', border: 'none', color: 'var(--text-4)', cursor: 'pointer', fontSize: 13, padding: '0 0 0 4px' }}>×</button>
              </div>
            ))}
          </div>
        )}
        {/* Searchable dropdown: shows all known islands + lets you add new */}
        <SearchableSelect
          value=""
          options={allDestinations.filter(d => !form.islands?.includes(d))}
          onChange={v => {
            if (v && !form.islands?.includes(v)) {
              setForm({ ...form, islands: [...(form.islands || []), v] });
            }
          }}
          placeholder="Search islands or type a city name…"
          onAddNew={(name) => {
            const v = name.trim();
            if (v && !form.islands?.includes(v)) {
              setForm({ ...form, islands: [...(form.islands || []), v] });
            }
            return v;
          }}
          addNewLabel="Add"
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
        <div>
          {label('Start Date *')}
          <input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} style={inputStyle} />
        </div>
        <div>
          {label('End Date (optional, for range)')}
          <input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} style={inputStyle} />
        </div>
      </div>

      <div>
        {label(`Travelers (${form.travelers.length})`)}
        {form.travelers.length > 0 && (
          <div style={{ fontSize: 10, color: 'var(--text-3)', marginBottom: 6, display: 'inline-flex', alignItems: 'center', gap: 4 }}>Tap the <Icon name="crown" size={11} /> to mark the trip leader</div>
        )}
        {/* Existing travelers */}
        {form.travelers.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
            {form.travelers.map((t, i) => (
              <div key={i} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                background: t.is_leader ? '#3a2900' : (t.type === 'voter' ? 'var(--surface-2)' : '#1a1a2e'),
                border: `1px solid ${t.is_leader ? '#fbbf24' : 'var(--border)'}`,
                borderRadius: 16, padding: '3px 10px', fontSize: 12,
              }}>
                <button type="button" onClick={() => toggleLeader(i)} title={t.is_leader ? 'Remove as leader' : 'Mark as trip leader'}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 0, display: 'inline-flex', color: t.is_leader ? '#fbbf24' : 'var(--text-4)', opacity: t.is_leader ? 1 : 0.5 }}>
                  <Icon name="crown" size={13} />
                </button>
                <span style={{ color: 'var(--text-4)', lineHeight: 0, display: 'inline-flex' }}><Icon name={t.type === 'voter' ? 'vote' : 'edit'} size={11} /></span>
                <span style={{ color: t.is_leader ? '#fbbf24' : 'var(--text)', fontWeight: t.is_leader ? 600 : 400 }}>{t.name}</span>
                <button onClick={() => removeTraveler(i)} type="button"
                  style={{ background: 'none', border: 'none', color: 'var(--text-4)', cursor: 'pointer', fontSize: 14, padding: '0 0 0 4px' }}>×</button>
              </div>
            ))}
          </div>
        )}
        {/* Voter search */}
        <div style={{ position: 'relative', marginBottom: 8 }}>
          <input type="text" value={voterSearch} onChange={e => setVoterSearch(e.target.value)}
            placeholder="Search voters by name..."
            style={inputStyle} />
          {voterResults.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6, zIndex: 10, maxHeight: 200, overflowY: 'auto', marginTop: 2 }}>
              {voterResults.map(v => (
                <div key={v.id} onClick={() => addVoterTraveler(v)}
                  style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: 13 }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--border)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <span style={{ color: 'var(--text-strong)' }}>{v.name}</span>
                  {(v.island || v.ward) && <span style={{ color: 'var(--text-3)', marginLeft: 8, fontSize: 11 }}>{v.island}{v.ward ? ` · ${v.ward}` : ''}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Manual add */}
        <div style={{ display: 'flex', gap: 8 }}>
          <input type="text" value={manualName} onChange={e => setManualName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addManualTraveler())}
            placeholder="Or type a name manually..."
            style={{ ...inputStyle, flex: 1 }} />
          <button type="button" onClick={addManualTraveler}
            style={{ padding: '10px 16px', borderRadius: 6, border: '1px solid var(--border-2)', background: 'transparent', color: 'var(--text-2)', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            + Add
          </button>
        </div>
      </div>

      {/* Day-by-day itinerary */}
      <div>
        {label('Itinerary (day-by-day, optional)')}
        {(form.itinerary || []).length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
            {form.itinerary.map((d, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="date" value={d.date || ''} onChange={e => updateItinerary(i, 'date', e.target.value)} style={{ ...inputStyle, width: 160, flexShrink: 0 }} />
                <input type="text" value={d.plan || ''} onChange={e => updateItinerary(i, 'plan', e.target.value)} placeholder="Plan for the day…" style={{ ...inputStyle, flex: 1 }} />
                <button type="button" onClick={() => removeItinerary(i)} style={{ background: 'none', border: 'none', color: 'var(--text-4)', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}>×</button>
              </div>
            ))}
          </div>
        )}
        <button type="button" onClick={addItineraryDay} style={{ padding: '8px 14px', borderRadius: 8, border: '1px dashed var(--border-2)', background: 'transparent', color: 'var(--text-2)', fontSize: 13, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Icon name="plus" size={13} /> Add day
        </button>
      </div>

      {/* Logistics checklist */}
      <div>
        {label('Logistics checklist (optional)')}
        {(form.checklist || []).length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
            {form.checklist.map((c, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Icon name="check" size={14} color={c.done ? '#10b981' : 'var(--text-4)'} />
                <input type="text" value={c.label || ''} onChange={e => updateChecklist(i, e.target.value)} placeholder="e.g. Book boat, accommodation, materials" style={{ ...inputStyle, flex: 1 }} />
                <button type="button" onClick={() => removeChecklist(i)} style={{ background: 'none', border: 'none', color: 'var(--text-4)', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}>×</button>
              </div>
            ))}
          </div>
        )}
        <button type="button" onClick={addChecklistItem} style={{ padding: '8px 14px', borderRadius: 8, border: '1px dashed var(--border-2)', background: 'transparent', color: 'var(--text-2)', fontSize: 13, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Icon name="plus" size={13} /> Add item
        </button>
      </div>

      <div>
        {label('Total Cost (MVR)')}
        <input type="number" value={form.cost} onChange={e => setForm({ ...form, cost: e.target.value })} style={inputStyle} />
      </div>

      <div>
        {label('Notes')}
        <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3}
          style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }} />
      </div>

      <div style={{ display: 'flex', gap: '12px' }}>
        <button onClick={onSubmit} disabled={disabled} style={{
          flex: 1, padding: '12px', borderRadius: '8px', border: 'none',
          background: disabled ? 'var(--border-2)' : '#10b981', color: 'white',
          fontSize: '14px', fontWeight: '600', cursor: disabled ? 'not-allowed' : 'pointer',
        }}>{submitLabel}</button>
        {onCancel && (
          <button onClick={onCancel} style={{
            padding: '12px 20px', borderRadius: '8px', border: '1px solid var(--border-2)',
            background: 'transparent', color: 'var(--text-2)', fontSize: '14px', cursor: 'pointer',
          }}>Cancel</button>
        )}
      </div>
    </div>
  );
}
